import { apiRequest } from "@/lib/api";
import { getDataClient } from "@/lib/dataClient";
import { useDataQuery, getCurrentUserId } from "@/hooks/useDataQuery";

// Ödeme & abonelik veri katmanı (PayTR + Havale/EFT).

export type PaymentMethod = "paytr" | "havale";
export type PaymentStatus = "pending" | "paid" | "failed" | "cancelled";

export interface Payment {
  id: string;
  packageId: string;
  packageName: string;
  amount: number;
  method: PaymentMethod;
  status: PaymentStatus;
  createdAt: string;
  paidAt: string;
}

export interface AdminPayment extends Payment {
  userId: string;
  userName: string;
  installmentTotal: number | null;
  installmentNumber: number | null;
  dueDate: string | null;
}

export const PAYMENT_STATUS_LABELS: Record<string, string> = {
  pending: "Bekliyor",
  paid: "Ödendi",
  failed: "Başarısız",
  cancelled: "İptal",
};

export const PAYMENT_STATUS_COLORS: Record<string, string> = {
  pending: "bg-secondary-100 text-secondary-900",
  paid: "bg-accent-100 text-accent-800",
  failed: "bg-red-50 text-red-700",
  cancelled: "bg-background-100 text-foreground-400",
};

export const PAYMENT_METHOD_LABELS: Record<string, string> = {
  paytr: "Kredi Kartı (PayTR)",
  havale: "Havale / EFT",
};

export interface Subscription {
  id: string;
  packageId: string;
  packageName: string;
  status: string;
  startsAt: string;
  endsAt: string;
}

function shortDate(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });
}

// Kullanıcının kendi ödemeleri.
export function useMyPayments() {
  return useDataQuery<Payment[]>(async () => {
    const dataClient = getDataClient();
    const userId = await getCurrentUserId();
    if (!userId) return [];
    const { data, error } = await dataClient
      .from("payments")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false });
    if (error) throw error;
    return (data ?? []).map(
      (p: { id: string; package_id: string | null; package_name: string | null; amount: number | null; method: string; status: string; created_at: string; paid_at: string | null }) => ({
        id: p.id,
        packageId: p.package_id ?? "",
        packageName: p.package_name ?? "",
        amount: Number(p.amount ?? 0),
        method: (p.method as PaymentMethod) ?? "havale",
        status: (p.status as PaymentStatus) ?? "pending",
        createdAt: shortDate(p.created_at),
        paidAt: shortDate(p.paid_at ?? ""),
      }),
    );
  });
}

// Kullanıcının aktif aboneliği.
export function useMySubscription() {
  return useDataQuery<Subscription | null>(async () => {
    const userId = await getCurrentUserId();
    if (!userId) return null;
    const result = await apiRequest<{packages: {id: string|null; user_id: string; product_code: string; package_name: string; status: string; starts_at: string|null; ends_at: string|null; active: boolean}[]}>('/account/packages');
    const s = result.packages.find(p => p.user_id === userId && p.active);
    if (!s) return null;
    return { id: s.id ?? '', packageId: s.product_code, packageName: s.package_name,
      status: s.status, startsAt: shortDate(s.starts_at ?? ''), endsAt: shortDate(s.ends_at ?? '') };

  });
}

// Yeni bir ödeme kaydı oluşturur (PayTR veya havale). RPC güvenli şekilde çalışır.
export async function createPayment(
  packageId: string,
  packageName: string,
  amount: number,
  method: PaymentMethod,
  billingCycle: "monthly" | "yearly" = "monthly",
  requestId: string = crypto.randomUUID(),
): Promise<{ id: string; merchantOid: string } | null> {
  const dataClient = getDataClient();
  const { data, error } = await dataClient.rpc("create_payment", {
    p_package_id: packageId,
    p_package_name: packageName,
    p_amount: amount,
    p_method: method,
    billing_cycle:billingCycle,
    request_id:requestId,
    expected_amount_cents:Math.round(amount*100),
  });
  if (error) throw new Error(error.message);
  const r = (data ?? {}) as { id?: string; merchant_oid?: string };
  return { id: r.id ?? "", merchantOid: r.merchant_oid ?? "" };
}

// Yönetici: havale ödemesini onaylar (aboneliği de aktive eder).
export async function confirmHavalePayment(paymentId: string): Promise<boolean> {
  const dataClient = getDataClient();
  const { error } = await dataClient.rpc("confirm_havale_payment", { p_payment_id: paymentId });
  return !error;
}

// Yönetici: tüm ödemeler.
export function useAllPayments() {
  return useDataQuery<AdminPayment[]>(async () => {
    const dataClient = getDataClient();
    const { data, error } = await dataClient
      .from("payments")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) throw error;

    const rows = (data ?? []) as {
      id: string;
      package_id: string | null;
      package_name: string | null;
      amount: number | null;
      method: string;
      status: string;
      created_at: string;
      paid_at: string | null;
      user_id: string;
      installment_total: number | null;
      installment_number: number | null;
      due_date: string | null;
    }[];

    const userIds = Array.from(new Set(rows.map((r) => r.user_id)));
    const nameMap = new Map<string, string>();
    if (userIds.length > 0) {
      const { data: profiles, error: pErr } = await dataClient
        .from("profiles")
        .select("user_id, full_name")
        .in("user_id", userIds);
      if (!pErr) {
        (profiles ?? []).forEach((p: { user_id: string | null; full_name: string | null }) => {
          if (p.user_id) nameMap.set(p.user_id, p.full_name ?? "Kullanıcı");
        });
      }
    }

    return rows.map((p) => ({
      id: p.id,
      packageId: p.package_id ?? "",
      packageName: p.package_name ?? "",
      amount: Number(p.amount ?? 0),
      method: (p.method as PaymentMethod) ?? "havale",
      status: (p.status as PaymentStatus) ?? "pending",
      createdAt: shortDate(p.created_at),
      paidAt: shortDate(p.paid_at ?? ""),
      userId: p.user_id,
      userName: nameMap.get(p.user_id) ?? "Kullanıcı",
      installmentTotal: p.installment_total ?? null,
      installmentNumber: p.installment_number ?? null,
      dueDate: shortDate(p.due_date ?? ""),
    }));
  });
}

// ----- Borçlu listesi + taksitlendirme (yönetici) -----

export interface Debtor {
  userId: string;
  userName: string;
  totalDebt: number;
  overdueDebt: number;
  pendingCount: number;
  installments: AdminPayment[];
}

export function useDebtors() {
  return useDataQuery<Debtor[]>(async () => {
    const dataClient = getDataClient();
    const { data, error } = await dataClient
      .from("payments")
      .select("*")
      .in("status", ["pending", "failed"])
      .order("due_date", { ascending: true });
    if (error) throw error;

    const rows = (data ?? []) as {
      id: string;
      user_id: string;
      package_id: string | null;
      package_name: string | null;
      amount: number | null;
      method: string;
      status: string;
      created_at: string;
      paid_at: string | null;
      installment_total: number | null;
      installment_number: number | null;
      due_date: string | null;
    }[];

    const userIds = Array.from(new Set(rows.map((r) => r.user_id)));
    const nameMap = new Map<string, string>();
    if (userIds.length > 0) {
      const { data: profiles, error: pErr } = await dataClient
        .from("profiles")
        .select("user_id, full_name")
        .in("user_id", userIds);
      if (!pErr) {
        (profiles ?? []).forEach((p: { user_id: string | null; full_name: string | null }) => {
          if (p.user_id) nameMap.set(p.user_id, p.full_name ?? "Kullanıcı");
        });
      }
    }

    const mapPayment = (p: (typeof rows)[number]): AdminPayment => ({
      id: p.id,
      packageId: p.package_id ?? "",
      packageName: p.package_name ?? "",
      amount: Number(p.amount ?? 0),
      method: (p.method as PaymentMethod) ?? "havale",
      status: (p.status as PaymentStatus) ?? "pending",
      createdAt: shortDate(p.created_at),
      paidAt: shortDate(p.paid_at ?? ""),
      userId: p.user_id,
      userName: nameMap.get(p.user_id) ?? "Kullanıcı",
      installmentTotal: p.installment_total ?? null,
      installmentNumber: p.installment_number ?? null,
      dueDate: shortDate(p.due_date ?? ""),
    });

    const byUser = new Map<string, AdminPayment[]>();
    const overdueByUser = new Map<string, number>();
    const nowMs = Date.now();
    rows.forEach((r) => {
      const list = byUser.get(r.user_id) ?? [];
      list.push(mapPayment(r));
      byUser.set(r.user_id, list);
      if (r.due_date && new Date(r.due_date).getTime() < nowMs) {
        overdueByUser.set(r.user_id, (overdueByUser.get(r.user_id) ?? 0) + Number(r.amount ?? 0));
      }
    });

    return Array.from(byUser.entries()).map(([userId, installments]) => ({
      userId,
      userName: installments[0]?.userName ?? "Kullanıcı",
      totalDebt: installments.reduce((s, i) => s + i.amount, 0),
      overdueDebt: overdueByUser.get(userId) ?? 0,
      pendingCount: installments.length,
      installments,
    }));
  });
}

export async function adminCreateInstallments(input: {
  userId: string;
  packageId: string;
  packageName: string;
  totalAmount: number;
  installmentCount: number;
  firstDueDate: string;
}): Promise<boolean> {
  const dataClient = getDataClient();
  const { error } = await dataClient.rpc("admin_create_installments", {
    p_user_id: input.userId,
    p_package_id: input.packageId || null,
    p_package_name: input.packageName,
    p_total_amount: input.totalAmount,
    p_installment_count: input.installmentCount,
    p_first_due_date: input.firstDueDate,
  });
  return !error;
}

export async function adminMarkInstallmentPaid(paymentId: string): Promise<boolean> {
  const dataClient = getDataClient();
  const { error } = await dataClient.rpc("admin_mark_installment_paid", { p_payment_id: paymentId });
  return !error;
}
